/**
 * First-visit cookie-consent banner (#846).
 *
 * Ported from `meetabl-ui`'s `ConsentBanner` (meetabl-ui#566), but the two products differ in ways
 * that matter more than the shared shape, so the divergences are listed here rather than left for
 * someone to discover by diffing:
 *
 * **1. Opt-in everywhere. No jurisdiction defaulting.** Meetabl treats US-outside-California as
 * opt-out and loads analytics by default there, under the CCPA model. OfficeA11y must not: the
 * Privacy Policy says, without qualification, that "no analytics script is loaded, and no analytics
 * cookie is set, until you consent". Porting Meetabl's defaulting would make that sentence false
 * for some visitors and not others, which is the hardest kind of breach to notice. Consequently
 * `useUserRegion` — which exists in this codebase and is what Meetabl keys its defaults off — is
 * deliberately NOT used here. There is nothing for it to decide.
 *
 * **2. "Accept" means analytics, not everything.** The Security FAQ states "No marketing or
 * advertising trackers." An Accept-all button setting `marketing: true` would record consent for a
 * category this product does not have, and would imply on the record that it does. Analytics is the
 * only non-necessary category in use, so it is the only one the banner turns on. Granular control
 * over the rest stays on /cookie-preferences.
 *
 * **3. Server only; no local consent cache.** Meetabl caches the decision in the browser so scripts
 * can be gated at first paint rather than after an API round-trip. That matters when the default is
 * "loaded" — you need to know quickly whether to *unload*. Here the default is that nothing loads,
 * so a slow lookup can only ever delay switching analytics ON, never delay switching it off. The
 * cache would buy nothing for privacy and would add a second place consent is stored, free to drift
 * from the server's record.
 *
 * This component owns the initial consent read for the whole app. It used to live in `App.tsx` as
 * `useAnalyticsConsent`; having both would mean two lookups racing, and a late-resolving one could
 * call `syncAnalyticsConsent(false)` moments after the visitor pressed Accept, silently undoing it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { apiService } from '../../services/api';
import { syncAnalyticsConsent } from '../../utils/analytics';

import './ConsentBanner.css';

/** Routes that render their own consent UI — the fixed banner must not overlay them. */
const SUPPRESSED_PREFIXES = ['/cookie-preferences', '/legal'];

/**
 * Any open modal dialog. Every modal in this app (GettingStartedVideo, ConfirmDialog, AlertDialog,
 * the billing and remediation dialogs, the webhook delivery detail) is mounted only while it is
 * open, so the attribute's presence is the open state.
 */
const MODAL_SELECTOR = '[aria-modal="true"]';

const modalIsOpen = (): boolean => document.querySelector(MODAL_SELECTOR) !== null;

/**
 * Whether a modal dialog is open anywhere on the page (OA-112).
 *
 * The banner is fixed to the viewport above every modal overlay — it has to be, to stay above the
 * app shell, whose header and sidebar share the modals' z-index. So while a dialog was open the
 * banner painted over its lower edge: the Getting Started dialog's "Got it, let's go!" button sat
 * underneath it, unclickable and entirely hidden when focused, which is the SC 2.4.11 failure this
 * banner was built to avoid. Lowering the z-index cannot fix that without sinking the banner below
 * the shell, so the banner yields instead.
 *
 * Observed rather than coordinated through context because the dialogs are independent components
 * with no shared state, and a new one should be covered without anyone remembering to wire it up.
 *
 * Only while `active`. The observer sees every mutation in the document, and most visitors have
 * already decided and will never see the banner; they should not pay for watching on their behalf.
 *
 * The answer is read from the document at render time; the state only triggers a re-render when it
 * changes. Returning the state itself would be stale on the render where `active` turns true — the
 * observer was off until then — and a dialog that opened while the consent lookup was in flight
 * (Getting Started does, straight after login) would get one render of banner, enough for the
 * banner to pull focus out of the dialog's trap.
 */
function useModalOpen(active: boolean): boolean {
  const [, setOpen] = useState(false);

  useEffect(() => {
    if (!active) {
      return;
    }
    const update = () => {
      setOpen(modalIsOpen());
    };
    // Sync the trigger with the document first. It has been idle while inactive, and a dialog that
    // is already open would otherwise leave it at false — so the close, which also sets false,
    // would change nothing and the banner would never re-render to come back.
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal'],
    });
    return () => {
      observer.disconnect();
    };
  }, [active]);

  return active && modalIsOpen();
}

/**
 * What each button records. `necessary` is always true; everything else stays false unless the
 * product actually uses it, which today means analytics alone.
 */
const ACCEPT_ANALYTICS = {
  necessary: true,
  analytics: true,
  marketing: false,
  preferences: false,
  functional: false,
};

const NECESSARY_ONLY = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
  functional: false,
};

export default function ConsentBanner(): React.ReactElement | null {
  const { t } = useTranslation('gdpr');
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bannerRef = useRef<HTMLElement>(null);
  const hasTakenFocus = useRef(false);
  const onConsentRoute = SUPPRESSED_PREFIXES.some(prefix => location.pathname.startsWith(prefix));
  const modalOpen = useModalOpen(visible);
  // Not rendered, no space reserved, no focus taken. Either the page owns consent UI itself, or a
  // modal dialog is open and the banner would paint over it (OA-112).
  const suppressed = onConsentRoute || modalOpen;

  useEffect(() => {
    // Object flag rather than a bare `let`, so the async closure reads live state instead of a
    // value control-flow analysis has narrowed to a constant.
    const state = { cancelled: false };

    void (async () => {
      let decided = false;
      let analytics = false;
      let marketing = false;
      try {
        const consent = await apiService.getCurrentConsent();
        // `hasConsent` is the API's own statement about whether a decision exists. An unknown
        // visitor comes back with hasConsent false and every category but `necessary` false, so
        // reading the categories alone could not tell "declined analytics" from "never asked".
        decided = consent?.hasConsent === true;
        analytics = consent?.categories?.analytics === true;
        marketing = consent?.categories?.marketing === true;
      } catch {
        // A failed lookup is NOT a decision. Fail closed on the script, and prompt: the visitor can
        // still choose, and the choice is recorded on the next successful call.
        decided = false;
        analytics = false;
        marketing = false;
      }
      if (state.cancelled) {
        return;
      }
      syncAnalyticsConsent(decided && analytics, decided && marketing);
      setVisible(!decided);
    })();

    return () => {
      state.cancelled = true;
    };
  }, []);

  // Move focus to the banner when it appears, so keyboard and AT users are not stranded at the top
  // of a page whose controls have just changed. Not while suppressed — it is not rendered then.
  //
  // After that, only when focus has been stranded. When suppression lifts because a dialog closed,
  // useModalFocus has just restored focus to whatever opened the dialog (SC 2.4.3), and taking it
  // here would undo that on every dialog in the app for as long as no decision has been made. But a
  // dialog that opened on its own — Getting Started, straight after login — has no opener still in
  // the document, so its restore does nothing and focus falls to <body>. Leaving it there strands a
  // keyboard user at the top of the page with the banner's controls below.
  useEffect(() => {
    if (!visible || suppressed || !headingRef.current) {
      return;
    }
    const focusStranded = !document.activeElement || document.activeElement === document.body;
    if (!hasTakenFocus.current || focusStranded) {
      hasTakenFocus.current = true;
      headingRef.current.focus();
    }
  }, [visible, suppressed]);

  // Reserve the space the fixed banner covers, for WCAG 2.2 SC 2.4.11 Focus Not Obscured
  // (Minimum).
  //
  // The banner is `position: fixed; bottom: 0`, so without this it paints over whatever has focus
  // near the foot of the viewport. Measured against this component's own CSS at 1280x800: a button
  // at the end of the document was 100% covered, and `document.elementFromPoint` at the centre of
  // the focused control returned `consent-banner__actions` rather than the control. Suppressing the
  // banner on the two routes that own consent UI does nothing for this — it is every *other* page
  // that has the problem — and the `width <= 599px` rule stacks the actions into a column, which
  // makes the banner taller and the overlap worse.
  //
  // `scroll-padding-bottom` alone is NOT enough, which is the part that is easy to get wrong: at
  // the end of the document there is no scroll distance left for it to use, and the element stays
  // covered. Reserving real space with padding is what moves the content; the scroll padding then
  // keeps it clear when the browser scrolls focus into view. Both measured: padding + scroll
  // padding together take the overlap to 0 and `elementFromPoint` returns the focused control.
  //
  // The height is observed rather than assumed because it changes — the actions stack below 600px
  // and the body text wraps.
  useEffect(() => {
    if (!visible || suppressed) {
      return;
    }
    const banner = bannerRef.current;
    if (!banner) {
      return;
    }

    const root = document.documentElement;
    const previousPadding = document.body.style.paddingBottom;
    const previousScrollPadding = root.style.scrollPaddingBottom;

    const reserve = () => {
      const height = Math.ceil(banner.getBoundingClientRect().height);
      document.body.style.paddingBottom = `${height}px`;
      root.style.scrollPaddingBottom = `${height}px`;
    };

    reserve();
    const observer = new ResizeObserver(reserve);
    observer.observe(banner);

    return () => {
      observer.disconnect();
      // Restore exactly what was there, so this never leaks padding onto a page that has none.
      document.body.style.paddingBottom = previousPadding;
      root.style.scrollPaddingBottom = previousScrollPadding;
    };
  }, [visible, suppressed]);

  const saveDecision = useCallback((preferences: typeof ACCEPT_ANALYTICS) => {
    // Gate scripts on the local decision immediately; do not wait for the server to acknowledge.
    syncAnalyticsConsent(preferences.analytics, preferences.marketing);
    // Persistence is best effort. If it fails the visitor is asked again next visit, which is
    // preferable to blocking the UI on it or pretending it succeeded.
    apiService.updateConsent(preferences).catch(() => {});
    setVisible(false);
  }, []);

  // Hidden while a page renders its own consent UI, so there are not two sets of consent controls
  // competing on one page. This is NOT what satisfies SC 2.4.11 — the banner would still cover
  // focused content on every other route. The space reservation above is what does that.
  //
  // Hidden while a modal dialog is open, too. Space reservation cannot help there: the dialog is
  // itself fixed to the viewport, so padding on <body> moves nothing out from under the banner.
  if (!visible || suppressed) {
    return null;
  }

  return (
    <section ref={bannerRef} className="consent-banner" aria-labelledby="consent-banner-heading">
      <div className="consent-banner__inner">
        <h2 id="consent-banner-heading" ref={headingRef} tabIndex={-1}>
          {t('banner.heading')}
        </h2>
        <p className="consent-banner__body">
          {t('banner.body')}{' '}
          <RouterLink to="/cookie-preferences">{t('banner.manageLink')}</RouterLink>.
        </p>
        {/* The global .btn classes from App.css, not bespoke ones: they carry the design
            system's focus ring and contrast pairings, both of which this banner would otherwise
            have to re-derive and could get wrong. */}
        <div className="consent-banner__actions">
          <RouterLink className="btn btn-ghost" to="/cookie-preferences">
            {t('banner.manage')}
          </RouterLink>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              saveDecision(NECESSARY_ONLY);
            }}
          >
            {t('banner.rejectNonEssential')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              saveDecision(ACCEPT_ANALYTICS);
            }}
          >
            {t('banner.acceptAnalytics')}
          </button>
        </div>
      </div>
    </section>
  );
}
